/**
 * LandingFranchises — public Signature FICO availability showcase.
 */
import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import api from '../../utils/api';
import { uploadHref } from '../../utils/uploadHref';

const STATUS_STYLES = {
    Available: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    Booked: 'bg-amber-50 text-amber-800 ring-amber-200',
    Blocked: 'bg-rose-50 text-rose-700 ring-rose-200',
};

function FranchiseCard({ item, delay }) {
    return (
        <article
            className="group overflow-hidden rounded-2xl bg-white ring-1 ring-ink-100 shadow-[0_8px_32px_-14px_rgba(15,17,21,0.1)] transition hover:ring-brand-teal/20"
            data-aos="fade-up"
            data-aos-delay={String(delay)}
        >
            <div className="relative aspect-[16/10] overflow-hidden bg-ink-100">
                {item.image ? (
                    <img
                        src={uploadHref(item.image)}
                        alt={item.displayName}
                        className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.04]"
                        loading="lazy"
                    />
                ) : (
                    <div className="grid h-full w-full place-items-center bg-gradient-to-br from-brand-teal-50 to-ink-50">
                        <MapPin className="h-10 w-10 text-brand-teal/30" strokeWidth={1.5} />
                    </div>
                )}
                <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ring-1 ${STATUS_STYLES[item.status] || STATUS_STYLES.Available}`}>
                    {item.status}
                </span>
            </div>
            <div className="p-4">
                <h3 className="font-heading text-base font-bold leading-snug text-ink-900">{item.displayName}</h3>
                {item.description && (
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-ink-500">{item.description}</p>
                )}
            </div>
        </article>
    );
}

export default function LandingFranchises() {
    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get('/franchises/public', { params: { type: 'signatureFico' } })
            .then((r) => setItems(r.data?.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoaded(true));
    }, []);

    if (!loaded || items.length === 0) return null;

    return (
        <section id="franchises" className="relative scroll-mt-24 overflow-hidden bg-ink-50 border-t border-ink-100/80">
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,rgba(0,176,155,0.06),transparent_55%)]"
            />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
                <div className="mx-auto max-w-3xl text-center" data-aos="fade-up">
                    <span className="lp-chip mb-6">Franchise opportunities</span>
                    <h2 className="font-heading text-3xl font-extrabold tracking-tight text-ink-900 sm:text-4xl lg:text-[2.65rem]">
                        Explore{' '}
                        <span className="bg-gradient-to-r from-brand-teal to-brand-teal-700 bg-clip-text text-transparent">
                            Signature FICO
                        </span>
                    </h2>
                    <p className="mt-5 text-base leading-relaxed text-ink-600 sm:text-lg">
                        Browse Signature FICO locations across India — updated in real time.
                    </p>
                </div>

                <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {items.map((item, i) => (
                        <FranchiseCard key={item._id} item={item} delay={80 + (i % 8) * 50} />
                    ))}
                </div>
            </div>
        </section>
    );
}
